import * as Sentry from '@sentry/react';
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { ApolloError } from '@apollo/client';
import { xTenantId } from '../../consts';
import { useUserProfileLazyQuery } from '../../libs/graphql/baseAppAPI/queries/__generated__/user-profile.query.generated';
import { UserCompliance, UserConsents, UserProfileResponse, LastLogins, UserKpis } from '../../hooks/registration';
import { BRANDS } from '../../components/Itv/types';

interface Props {
  children: React.ReactNode;
}

interface ContextValue {
  fetching: boolean;
  error: ApolloError | undefined;
  user: UserProfileResponse | undefined | null;
  consents: UserConsents | undefined | null;
  lastLogins: LastLogins | undefined | null;
  compliance: UserCompliance | undefined | null;
  kpIs: UserKpis | undefined | null;
  userProfileId: string;
  fetchUserProfile: () => Promise<void>;
  theme: BRANDS;
  setTheme: (arg: BRANDS) => void;
}

const initialValue: ContextValue = {
  fetching: false,
  user: undefined,
  error: undefined,
  compliance: undefined,
  consents: undefined,
  lastLogins: undefined,
  kpIs: undefined,
  userProfileId: '',
  fetchUserProfile: async () => undefined,
  theme: BRANDS.BINGO,
  setTheme: () => {},
};

const UserContext = React.createContext(initialValue);

export const useUserContext = (): ContextValue => useContext(UserContext);

export const UserProvider: FC<Props> = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth?.user?.access_token || '';

  const [getUserProfile, { data, loading, error }] = useUserProfileLazyQuery({ fetchPolicy: 'network-only' });

  const [theme, setTheme] = useState<BRANDS>(BRANDS.BINGO);

  const fetchUserProfile = useCallback(async (): Promise<void> => {
    await getUserProfile({
      variables: {
        userProfileId: (auth.user?.profile?.user_profile_id as string) || '',
        xTenantId,
      },
    });
  }, [auth.user?.profile?.user_profile_id, getUserProfile]);

  useEffect(() => {
    if (auth?.isAuthenticated && accessToken && !data?.userProfile.user) {
      fetchUserProfile();
    }
  }, [auth?.isAuthenticated, accessToken, fetchUserProfile, data?.userProfile.user]);

  useEffect(() => {
    const profileId = auth.user?.profile?.user_profile_id as string | undefined;

    if (auth?.isAuthenticated && profileId) {
      Sentry.setUser({ id: profileId });
    } else {
      Sentry.setUser(null);
    }
  }, [auth?.isAuthenticated, auth.user?.profile?.user_profile_id]);

  const value: ContextValue = useMemo(() => {
    return {
      fetching: loading,
      user: data?.userProfile.user,
      consents: data?.userProfile.consents,
      lastLogins: data?.userProfile.lastLogins,
      compliance: data?.userProfile.compliance,
      kpIs: data?.userProfile.kpIs,
      userProfileId: (auth.user?.profile?.user_profile_id as string) || '',
      error,
      fetchUserProfile,
      theme,
      setTheme,
    };
  }, [data, loading, auth.user?.profile?.user_profile_id, error, fetchUserProfile, theme]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
